import classNames from "classnames";
import React, { ReactNode } from "react";

export type BadgeColor =
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "danger";

export type BadgeVariant =
  | "archived"
  | "time-work"
  | "time-work-task"
  | "status-green"
  | "status-grey"
  | "outline"
  | "filled";

export type BadgeSize = "xs" | "sm" | "md" | "lg";

type BadgeProps = {
  label: ReactNode;
  variant: BadgeVariant;
  size?: BadgeSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  withDot?: boolean;
  className?: string;
  labelClassName?: string;
  dotClassName?: string;
  color?: BadgeColor;
};

const BADGE_VARIANT_CLASSES: Record<BadgeVariant, string> = {
  archived: "bg-[#FFF4E5] text-[#F2994A]",
  "time-work": "bg-[#EDEBFE] text-[#8A6FF9]",
  "time-work-task": "bg-[#EEFBF1] text-[#06C96C]",
  "status-green": "border border-[#C5EDCC] bg-[#F1FCF1] text-[#0E5943]",
  "status-grey": "border border-[#E4E9F2] bg-[#F5F7FA] text-[#253747]",
  outline: "",
  filled: "",
};

const BADGE_OUTLINE_COLORS: Record<BadgeColor, string> = {
  primary: "border border-gray-200 text-[#253747]",
  secondary: "border border-gray-200 text-[#7C66F0]",
  success: "border border-[#EEFBF1] text-[#06C96C]",
  warning: "border border-[#FFF4E5] text-[#F2994A]",
  danger: "border",
};

const BADGE_FILLED_COLORS: Record<BadgeColor, string> = {
  primary: "bg-gray-200 text-[#253747]",
  secondary: "bg-[#EDEBFE] text-[#8A6FF9]",
  success: "bg-[#EEFBF1] text-[#06C96C]",
  warning: "bg-[#FFF4E5] text-[#F2994A]",
  danger: "",
};

const DOT_CLASSES: Partial<Record<BadgeVariant, string>> = {
  archived: "bg-[#F2994A]",
  "status-green": "bg-[#0E5943]",
  "status-grey": "bg-[#A1AFC0]",
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  xs: "h-5 px-2 text-[11px]",
  sm: "h-6 px-3 text-xs",
  md: "h-7 px-3 text-sm",
  lg: "h-8 px-4 text-sm",
};

export const Badge: React.FC<BadgeProps> = ({
  label,
  variant,
  size = "sm",
  leftIcon,
  rightIcon,
  withDot,
  className,
  labelClassName,
  dotClassName,
  color = "primary",
}) => {
  const shouldShowDot = withDot || variant.startsWith("status-");

  return (
    <span
      className={classNames(
        "inline-flex w-fit cursor-default items-center justify-center gap-2 rounded-lg font-Manrope font-medium leading-none",
        SIZE_CLASSES[size],
        BADGE_VARIANT_CLASSES[variant],
        variant === "outline" && BADGE_OUTLINE_COLORS[color],
        variant === "filled" && BADGE_FILLED_COLORS[color],
        className,
      )}>
      {leftIcon}
      {shouldShowDot && (
        <span
          className={classNames(
            "h-1.5 w-1.5 rounded-full",
            DOT_CLASSES[variant],
            dotClassName,
          )}
        />
      )}
      <span className={classNames("whitespace-nowrap", labelClassName)}>
        {label}
      </span>
      {rightIcon}
    </span>
  );
};
